/* eslint-disable react/jsx-no-duplicate-props */
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import ViewIcon from '@mui/icons-material/RemoveRedEye';
import Router from 'next/router';
import React, { useEffect, useState } from 'react';
import isURL from 'validator/lib/isURL';
import { GET_PROFILE } from '../../helpers/graphql/profile';
import graphQLClient from '../../helpers/graphQLClient';
import {
  validFacebookUsername,
  validInstagramUsername,
  validPinterestUsername,
  validTwitterUsername,
} from '../../helpers/regex';
import { getUser } from '../../helpers/token';
import usePreventRouteChangeIf from '../../helpers/usePreventRouteChangeIf';
import Link from '@mui/material/Link';
import FeaturedImageUpload from '../Editor/FeaturedImageUpload';
import ConfirmDialog from '../General/ConfirmDialog';
import DashboardCard from './DashboardCard';
import FormWithDescription from './FormWithDescription';
import UpdateProfileButton from './Profile/UpdateProfileButton';

const Profile = () => {
  const author = getUser();

  const [displayName, setDisplayName] = useState('');
  const [about, setAbout] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [website, setWebsite] = useState('');
  const [facebook, setFacebook] = useState('');
  const [twitter, setTwitter] = useState('');
  const [instagram, setInstagram] = useState('');
  const [pinterest, setPinterest] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [navigateTo, setNavigateTo] = useState(false);

  useEffect(() => {
    graphQLClient(GET_PROFILE, { username: author }).then(({ profile }) => {
      if (!profile) return;
      if (profile.author && profile.author.displayName && !displayName)
        setDisplayName(profile.author.displayName);
      if (profile.about && !about) setAbout(profile.about);
      if (profile.author && profile.author.avatar && !profileImage)
        setProfileImage(profile.author.avatar);
      if (profile.coverImage && !coverImage) setCoverImage(profile.coverImage);
      if (profile.website && !website) setWebsite(profile.website);
      if (profile.facebook && !facebook) setFacebook(profile.facebook);
      if (profile.twitter && !twitter) setTwitter(profile.twitter);
      if (profile.instagram && !instagram) setInstagram(profile.instagram);
      if (profile.pinterest && !pinterest) setPinterest(profile.pinterest);
    });
  }, []);

  usePreventRouteChangeIf(hasUnsavedChanges, url => {
    setNavigateTo(url);
  });

  const handleInput = changeThis => event => {
    changeThis(event.target.value);
    setHasUnsavedChanges(true);
  };

  const handleImgInput = changeThis => newImg => {
    changeThis(newImg);
    setHasUnsavedChanges(true);
  };

  const handleNavigate = async () => {
    setHasUnsavedChanges(false);
    setNavigateTo(false);
    await new Promise(r => setTimeout(r, 1));
    Router.push(navigateTo);
  };

  const isTwitterValid = !twitter || twitter.match(validTwitterUsername);
  const isFacebookValid = !facebook || facebook.match(validFacebookUsername);
  const isInstagramValid =
    !instagram || instagram.match(validInstagramUsername);
  const isPinterestValid =
    !pinterest || pinterest.match(validPinterestUsername);
  const isWebsiteValid = !website || isURL(website);

  return (
    <>
      <div className="container d-flex justify-content-center mt-5 mb-5">
        <div style={{ maxWidth: '800px' }}>
          <DashboardCard
            titleSize="h5"
            title="Edit your profile"
            cardActions={
              <div className="container">
                <div className="row">
                  <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 p-1">
                    <UpdateProfileButton
                      afterUpdate={() => setHasUnsavedChanges(false)}
                      isError={
                        !isTwitterValid ||
                        !isFacebookValid ||
                        !isPinterestValid ||
                        !isInstagramValid ||
                        !isWebsiteValid
                      }
                      variables={{
                        profile: {
                          author: { displayName, avatar: profileImage },
                          coverImage,
                          about,
                          website,
                          twitter,
                          facebook,
                          instagram,
                          pinterest,
                        },
                      }}
                    />
                  </div>
                  <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 p-1">
                    <Link color="textPrimary" href={`/@${getUser()}`}>
                      <Button fullWidth color="primary" variant="outlined">
                        <ViewIcon />
                        <span className="pl-2"> View profile </span>
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            }
            content={
              <>
                <div className="container cardContainer">
                  <FormWithDescription
                    title="About"
                    description="A cool name to show instead of your username and your one-line biography"
                  >
                    <TextField
                      variant="standard"
                      label="Name"
                      inputProps={{
                        maxLength: 20,
                      }}
                      placeholder="A cool name to show instead of your username"
                      margin="normal"
                      value={displayName}
                      onChange={handleInput(setDisplayName)}
                      fullWidth
                    />
                    <TextField
                      variant="standard"
                      multiline
                      label="Bio"
                      inputProps={{
                        maxLength: 160,
                      }}
                      placeholder="Your one-line biography goes here"
                      margin="normal"
                      value={about}
                      onChange={handleInput(setAbout)}
                      fullWidth
                    />
                  </FormWithDescription>
                  <FormWithDescription
                    title="Social media"
                    description="Add your social media accounts here to show them in your profile"
                    marginTop
                  >
                    <TextField
                      variant="standard"
                      label="Facebook"
                      error={!isFacebookValid}
                      inputProps={{
                        maxLength: 50,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            https://facebook.com/
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Your Facebook username or fanpage"
                      margin="normal"
                      value={facebook}
                      onChange={handleInput(setFacebook)}
                      fullWidth
                    />
                    <TextField
                      variant="standard"
                      label="Twitter"
                      error={!isTwitterValid}
                      inputProps={{
                        maxLength: 16,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            https://twitter.com/
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Your Twitter username"
                      margin="normal"
                      value={twitter}
                      onChange={handleInput(setTwitter)}
                      fullWidth
                    />
                    <TextField
                      variant="standard"
                      label="Instagram"
                      error={!isInstagramValid}
                      inputProps={{
                        maxLength: 30,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            https://instagram.com/
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Your Instagram username"
                      margin="normal"
                      value={instagram}
                      onChange={handleInput(setInstagram)}
                      fullWidth
                    />
                    <TextField
                      variant="standard"
                      label="Pinterest"
                      error={!isPinterestValid}
                      inputProps={{
                        maxLength: 30,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            https://pinterest.com/
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Your Pinterest username"
                      margin="normal"
                      value={pinterest}
                      onChange={handleInput(setPinterest)}
                      fullWidth
                    />
                    <TextField
                      variant="standard"
                      label="Website"
                      error={!isWebsiteValid}
                      inputProps={{
                        maxLength: 100,
                      }}
                      placeholder="Link to your website"
                      margin="normal"
                      value={website}
                      onChange={handleInput(setWebsite)}
                      fullWidth
                    />
                  </FormWithDescription>
                  <FormWithDescription
                    title="Profile image"
                    description="Your avatar will be cut to a round shape automatically. Recommended dimensions: 400x400"
                    marginTop
                  >
                    <FeaturedImageUpload
                      rounded
                      featuredImage={profileImage}
                      setFeaturedImage={handleImgInput(setProfileImage)}
                      placeholder="To upload your profile image, drag 'n' drop an image here, or click to select one."
                    />
                  </FormWithDescription>
                  <FormWithDescription
                    title="Cover image"
                    description=" The cover image will be displayed on top of your
                  TravelFeed profile page. Recommended dimensions:
                  2880x600"
                    marginTop
                  >
                    <FeaturedImageUpload
                      featuredImage={coverImage}
                      setFeaturedImage={handleImgInput(setCoverImage)}
                      placeholder="To upload your cover image, drag 'n' drop an image here, or click to select one"
                    />
                  </FormWithDescription>
                </div>
              </>
            }
          />
        </div>
      </div>
      {navigateTo && (
        <ConfirmDialog
          btnText="Discard Changes"
          dialogText={`You have unsaved changes that will be discarded if you leave this page. If you want to save your changes, press "cancel" and use the "Update Profile" button on the bottom of the page.`}
          onConfirm={handleNavigate}
          open={navigateTo}
          setOpen={setNavigateTo}
        />
      )}
    </>
  );
};

export default Profile;
